import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from '../../app/store.types';
import { Footer } from '../../features/footer/Footer';
import { Header } from '../../features/header/Header';
import {
  ScreenMode,
  Tab,
  RatesFieldAuditTrailRow,
  RatesFixingAuditTrailRow,
  LegalEntityAuditTrailRow,
  AuditTrailsGrouped,
} from '../../common/domain/mappingAudit';
import {
  setActiveTab,
  setScreenMode,
} from '../../features/mappingAudit/mappingAuditSlice';
import type { GridApi, SelectionChangedEvent } from 'ag-grid-community';
import messages, { TAB_HEADER_MESSAGES } from './messages';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { Container, Row, Col, CardBody, Button, Tooltip } from 'reactstrap';
import { AuditGrid } from './components/AuditGrid';
import { withRequireMappingAdmin } from '../../common/authentication';
import { getMappingAuditTrail } from '../../api/mappingAudit';
import { getCsvProcessCellCallback } from '../MappingAdminPage/utils';
import { AUDIT_CSV_TIMESTAMP_COLUMNS } from '../MappingAdminPage/constants';
import { TABS_PATHS } from './constants';

const MappingAuditPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const gridApiRef = useRef<GridApi | null>(null);

  const activeTab = useSelector(
    (state: RootState) => state.mappingAudit.activeTab,
  );
  const screenMode = useSelector(
    (state: RootState) => state.mappingAudit.screenMode,
  );

  const [isLoading, setIsLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [selectedCount, setSelectedCount] = useState(0);
  const [exportTooltipIsOpen, setExportTooltipIsOpen] = useState(false);

  useEffect(() => {
    const tabFromUrl =
      (Object.keys(TABS_PATHS) as Array<keyof typeof TABS_PATHS>).find((tab) =>
        location.pathname.endsWith(TABS_PATHS[tab]),
      ) ?? Tab.RATES_FIELD;
    dispatch(setActiveTab(tabFromUrl));
  }, [dispatch, location.pathname]);

  const refreshSelectedCount = useCallback((api: GridApi) => {
    const selectedNodes = api.getSelectedNodes();
    const displayedSelected = selectedNodes.filter((n) => n.displayed);
    if (selectedNodes.length !== displayedSelected.length) {
      api.deselectAll();
      displayedSelected.forEach((n) => n.setSelected(true));
    }
    setSelectedCount(displayedSelected.length);
  }, []);

  const onSelectionChanged = (event: SelectionChangedEvent) => {
    refreshSelectedCount(event.api);
  };

  const [ratesFieldAuditTrailData, setRatesFieldAuditTrailData] = useState<
    RatesFieldAuditTrailRow[]
  >([]);
  const [ratesFixingAuditTrailData, setRatesFixingAuditTrailData] = useState<
    RatesFixingAuditTrailRow[]
  >([]);
  const [legalEntityAuditTrailData, setLegalEntityAuditTrailData] = useState<
    LegalEntityAuditTrailRow[]
  >([]);
  const enterSelectMode = () => {
    dispatch(setScreenMode(ScreenMode.SELECTING_MODE));
  };

  const exitSelectMode = () => {
    gridApiRef.current?.deselectAll();
    dispatch(setScreenMode(ScreenMode.VIEWING_MODE));
  };

  const exportSelectedRows = () => {
    if (selectedCount === 0) {
      setError(
        'No rows have been selected. Please select 1 or more rows to export.',
      );
    }

    const now = new Date();
    const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const csvFileName = `mapping-audit-${activeTab}-${timestamp}.csv`;

    gridApiRef.current?.exportDataAsCsv({
      onlySelected: true,
      fileName: csvFileName,
      processCellCallback: getCsvProcessCellCallback(
        AUDIT_CSV_TIMESTAMP_COLUMNS,
      ),
    });

    exitSelectMode();
  };

  useEffect(() => {
    const fetchMappingAuditTrail = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data: AuditTrailsGrouped = await getMappingAuditTrail();
        setRatesFieldAuditTrailData(
          Array.isArray(data.ratesField) ? data.ratesField : [],
        );
        setRatesFixingAuditTrailData(
          Array.isArray(data.ratesFixing) ? data.ratesFixing : [],
        );
        setLegalEntityAuditTrailData(
          Array.isArray(data.legalEntity) ? data.legalEntity : [],
        );
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load audit trail',
        );
        setRatesFieldAuditTrailData([]);
        setRatesFixingAuditTrailData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMappingAuditTrail();
  }, []);

  const rowData = useMemo(() => {
    switch (activeTab) {
      case Tab.RATES_FIELD:
        return ratesFieldAuditTrailData;
      case Tab.RATES_FIXING:
        return ratesFixingAuditTrailData;
      case Tab.LEGAL_ENTITY:
        return legalEntityAuditTrailData;
      default:
        return ratesFieldAuditTrailData;
    }
  }, [
    activeTab,
    ratesFieldAuditTrailData,
    ratesFixingAuditTrailData,
    legalEntityAuditTrailData,
  ]);

  useEffect(() => {
    gridApiRef.current?.sizeColumnsToFit();
  }, [screenMode, activeTab]);

  return (
    <>
      <Header />
      <Container fluid>
        <Helmet>
          <title>{messages.title.defaultMessage}</title>
          <meta name="description" content={messages.metaDesc.defaultMessage} />
        </Helmet>

        <Row>
          <Col>
            <h1 className="tpicap-h mb-1 ml-4">
              <FormattedMessage {...TAB_HEADER_MESSAGES[activeTab]} />
            </h1>
          </Col>
        </Row>
        <Row>
          <Col>
            <CardBody>
              <Row className="mb-2">
                {screenMode === ScreenMode.SELECTING_MODE && (
                  <span className="text-muted">
                    {selectedCount} row(s) selected
                  </span>
                )}
                <Col className="d-flex justify-content-end">
                  {screenMode === ScreenMode.SELECTING_MODE && (
                    <Button
                      color="secondary"
                      size="sm"
                      className="mr-2"
                      onClick={exitSelectMode}
                    >
                      Cancel
                    </Button>
                  )}
                  {screenMode === ScreenMode.SELECTING_MODE && (
                    <>
                      <span
                        id="export-tooltip-target"
                        className="d-inline-block"
                      >
                        <Button
                          color="primary"
                          size="sm"
                          onClick={exportSelectedRows}
                          disabled={selectedCount === 0}
                        >
                          Export to CSV
                        </Button>
                      </span>
                      {selectedCount === 0 && (
                        <Tooltip
                          target="export-tooltip-target"
                          isOpen={exportTooltipIsOpen}
                          toggle={() =>
                            setExportTooltipIsOpen(!exportTooltipIsOpen)
                          }
                        >
                          Select at least one row to export
                        </Tooltip>
                      )}
                    </>
                  )}
                  {screenMode === ScreenMode.VIEWING_MODE && (
                    <Button color="primary" size="sm" onClick={enterSelectMode}>
                      Export...
                    </Button>
                  )}
                </Col>
              </Row>
              <AuditGrid
                rowData={rowData}
                isLoading={isLoading}
                tab={activeTab}
                screenMode={screenMode}
                onGridReady={(api) => {
                  gridApiRef.current = api;
                }}
                onSelectionChanged={onSelectionChanged}
                onFilterChanged={refreshSelectedCount}
              />
            </CardBody>
          </Col>
        </Row>
      </Container>
      <Footer />
    </>
  );
};

export default withRequireMappingAdmin(MappingAuditPage);
